const DEFAULT_DELAY_MS = 500
const INITIAL_RETRY_MS = 5000
const MAX_RETRY_MS = 60000
const MAX_RETRIES = 8

export type RefreshContext = { isCurrent: () => boolean }

/**
 * Schedule a data reload, not an event handler: individual payloads must not be dropped.
 * Return the entire request promise and check isCurrent before applying a response.
 */
export function createRefreshScheduler (refresh: (context: RefreshContext) => PromiseLike<unknown>, delayMs = DEFAULT_DELAY_MS) {
  let timer: ReturnType<typeof setTimeout> | undefined
  let inFlight = false
  let pending = false
  let retryDelayMs = 0
  let retryAt = 0
  let failureCount = 0
  let pauseDepth = 0
  let generation = 0
  let disposed = false
  let pageCached = false

  function schedule () {
    if (disposed || timer !== undefined || inFlight || pauseDepth > 0 || !pending) return
    timer = setTimeout(runRefresh, Math.max(DEFAULT_DELAY_MS, delayMs, retryAt - Date.now()))
  }

  async function runRefresh () {
    timer = undefined
    pending = false
    inFlight = true
    const requestGeneration = generation
    try {
      await refresh({ isCurrent: () => !disposed && requestGeneration === generation })
      retryDelayMs = 0
      retryAt = 0
      failureCount = 0
    } catch {
      // Keep the existing view visible and retry, even if no further event arrives.
      // After the last retry, wait for the next request, which keeps the current backoff.
      failureCount++
      if (failureCount <= MAX_RETRIES) pending = true
      retryDelayMs = Math.min(MAX_RETRY_MS, retryDelayMs ? retryDelayMs * 2 : INITIAL_RETRY_MS)
      const jitteredDelay = Math.min(MAX_RETRY_MS, retryDelayMs * (1 + Math.random() * 0.25))
      retryAt = Date.now() + jitteredDelay
    } finally {
      inFlight = false
      schedule()
    }
  }

  const scheduler = {
    request () {
      if (disposed) return
      pending = true
      schedule()
    },
    pause () {
      pauseDepth++
      generation++
      clearTimeout(timer)
      timer = undefined
    },
    resume () {
      pauseDepth = Math.max(0, pauseDepth - 1)
      schedule()
    },
    dispose () {
      disposed = true
      pending = false
      clearTimeout(timer)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }

  function onPageHide (event: PageTransitionEvent) {
    if (event.persisted) {
      pageCached = true
      scheduler.pause()
      // A response invalidated while cached must be fetched again on restoration.
      if (inFlight) pending = true
    } else {
      scheduler.dispose()
    }
  }
  function onPageShow () {
    if (pageCached) {
      pageCached = false
      scheduler.resume()
    }
  }
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', onPageShow)
  return scheduler
}

